<template>
  <div>
    <v-toolbar density="compact" color="grey-lighten-3">
      <v-tabs v-model="tab">
        <v-tab
          v-for="(plan, i) in store.plan.individual_asset_management_plans"
          :key="i"
        >
          Plan {{ i + 1 }}
          <TooltipIcon
            v-if="!plan.valid"
            class="ml-2"
            text="Please fill out all fields."
          />
        </v-tab>
        <v-btn
          v-if="!readonly"
          style="margin-left: 4px; margin-top: 6px"
          variant="plain"
          color="secondary"
          @click="addPlan"
        >
          <v-icon>mdi-plus</v-icon>Add
        </v-btn>
      </v-tabs>
    </v-toolbar>
    <v-divider />
    <v-tabs-window :model-value="tab">
      <v-tabs-window-item
        v-for="(plan, i) in store.plan.individual_asset_management_plans"
        :value="i"
        :key="i"
      >
        <v-toolbar density="compact" color="grey-lighten-4">
          <v-toolbar-title>Plan {{ i + 1 }}</v-toolbar-title> <v-spacer />
          <v-btn
            v-if="!readonly"
            class="ma-2"
            icon="mdi-delete"
            variant="plain"
            color="red"
            @click="deletePlan(plan)"
          />
        </v-toolbar>
        <div class="pa-4">
          <RichText
            v-model="plan.management_consideration_factors"
            :label="`5.${i + 1}.1 Management Consideration Factors`"
            class="mb-4"
            required
            :readonly="readonly"
          />
          <RichText
            v-model="plan.condition_monitoring"
            :label="`5.${i + 1}.2 Condition Monitoring`"
            class="mb-4"
            required
            :readonly="readonly"
          />
          <RichText
            v-model="plan.maintenance_defect_management"
            :label="`5.${i + 1}.3 Maintenance & Defect Management`"
            class="mb-4"
            required
            :readonly="readonly"
          />
          <RichText
            v-model="plan.maintenance_plans"
            :label="`5.${i + 1}.4 Maintenance Plans`"
            class="mb-4"
            required
            :readonly="readonly"
          />
          <v-card class="mb-4" variant="outlined" rounded="lg">
            <v-toolbar density="compact">
              <v-toolbar-title>
                5.{{ i + 1 }}.5 Preventative Maintenance Tactics
              </v-toolbar-title>
              <v-spacer />
              <span>
                # Assets Applied:
                {{ store.preventativeMaintenance.num_assets_applied }} /
                {{ store.preventativeMaintenance.num_assets }},
              </span>
              <span class="ml-3">
                # Compliance:
                {{ store.preventativeMaintenance.num_workorders }} /
                {{ store.preventativeMaintenance.num_tactic_ops }}
              </span>
              <v-tooltip text="Refresh" location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="ml-3"
                    icon="mdi-reload"
                    density="compact"
                    :loading="store.fetchingPreventativeMaintenance"
                    @click="store.getPreventativeMaintenance()"
                  />
                </template>
              </v-tooltip>
            </v-toolbar>
            <v-table class="tactics-table" fixed-header density="compact">
              <thead>
                <tr>
                  <th class="text-left">Maint Plan ID</th>
                  <th class="text-left">Maint Plan Desc</th>
                  <th class="text-left">Asset</th>
                  <th class="text-left">Stat Flag</th>
                  <th class="text-left">Num Done (last 12 months)</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(tactic, i) in store.preventativeMaintenance.tactics"
                  :key="i"
                >
                  <td>{{ tactic.maint_plan_id }}</td>
                  <td>{{ tactic.maint_plan_description }}</td>
                  <td>{{ tactic.asset_name }}</td>
                  <td></td>
                  <td>
                    {{ tactic.num_workorders }} / {{ tactic.num_tactic_ops }}
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
          <RichText
            v-model="plan.statutary_requirements"
            :label="`5.${i + 1}.6 Statutary Requirements`"
            class="mb-4"
            required
            :readonly="readonly"
          />
          <RichText
            v-model="plan.equipment_decomissioning_disposal"
            :label="`5.${i + 1}.7 Equipment Decomissioning & Disposal`"
            class="mb-4"
            required
            :readonly="readonly"
          />
          <RichText
            v-model="plan.capital_projects"
            :label="`5.${i + 1}.8 Capital Projects`"
            class="mb-4"
            required
            :readonly="readonly"
          />
        </div>
      </v-tabs-window-item>
    </v-tabs-window>
  </div>
</template>

<script setup lang="ts">
import { IndividualAssetManagementPlan } from "@/classes";
import type { Deleteable } from "@/classes/asset-management-plan";
import { ref } from "vue";

import RichText from "@/components/base/inputs/RichText.vue";
import { useConfirmStore } from "@/store/confirm";
import TooltipIcon from "@/components/base/tooltips/TooltipIcon.vue";
import { useAMPStore } from "@/store/asset-management-plan";

const props = defineProps<{
  readonly: boolean;
}>();

const emit = defineEmits<{
  (e: "delete", val: Deleteable): void;
}>();

const store = useAMPStore();

const tab = ref(0);

const addPlan = () => {
  const plan = new IndividualAssetManagementPlan();
  plan.asset_management_plan = store.plan;
  plan.asset_management_plan_id = store.plan.id;
  store.plan.individual_asset_management_plans.push(plan);
};

const $confirm = useConfirmStore();

const deletePlan = (plan: IndividualAssetManagementPlan) => {
  $confirm.open({
    title: "Delete Plan?",
    confirmText: "Delete",
    color: "red",
    onConfirm: () => {
      if (
        tab.value ===
        store.plan.individual_asset_management_plans.length - 1
      ) {
        tab.value = Math.max(0, tab.value - 1);
      }
      store.plan.individual_asset_management_plans =
        store.plan.individual_asset_management_plans.filter(
          (p) => p.id !== plan.id
        );
      emit("delete", plan);
    },
  });
};
</script>

<style lang="scss" scoped>
.tactics-table {
  max-height: 400px;
}
</style>
